/**
 * One-click AI pull request review — shared contract between the Functions
 * BFF (which runs the model over the diff) and the SPA (which renders the
 * findings and lets the user publish them to GitHub).
 */
export const AI_REVIEW_LIMITS = {
    maxFindings: 20,
    summaryMaxChars: 2000,
    findingTextMaxChars: 1200,
    testingNotes: 8,
};
const SEVERITIES = ['critical', 'high', 'medium', 'low'];
const VERDICTS = ['approve', 'comment', 'request_changes'];
const EVENTS = ['COMMENT', 'APPROVE', 'REQUEST_CHANGES'];
export function aiReviewSeverityRank(severity) {
    return SEVERITIES.indexOf(severity);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Model output is untrusted (a PR author can steer it) and is later published
 * under the user's identity: drop HTML and markdown images so no tracking
 * pixel or markup smuggles through; plain links stay visible as text.
 */
export function stripUntrustedMarkup(value) {
    return (value
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/<[^>]*>/g, '')
        // Masked links render differently on GitHub than in the panel: unmask them.
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1 ($2)')
        .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
        // Forged headings/footers would fake the review's own structure.
        .replace(/^\s{0,3}#{1,6}\s+/gm, '')
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s{3,}/g, '  '));
}
function cleanText(value, max) {
    return typeof value === 'string' ? stripUntrustedMarkup(value).trim().slice(0, max) : '';
}
function parseSeverity(value) {
    const lowered = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return SEVERITIES.includes(lowered)
        ? lowered
        : 'medium';
}
function parseFinding(raw) {
    if (!isRecord(raw))
        return null;
    const path = cleanText(raw.path ?? raw.file, 300);
    const title = cleanText(raw.title, 200);
    const detail = cleanText(raw.detail ?? raw.message ?? raw.body, AI_REVIEW_LIMITS.findingTextMaxChars);
    if (!title && !detail)
        return null;
    const line = Number(raw.line);
    const suggestion = cleanText(raw.suggestion, AI_REVIEW_LIMITS.findingTextMaxChars);
    return {
        path: path || '(general)',
        ...(Number.isInteger(line) && line > 0 ? { line } : {}),
        severity: parseSeverity(raw.severity),
        title: title || detail.slice(0, 80),
        detail: detail || title,
        ...(suggestion ? { suggestion } : {}),
    };
}
/**
 * Validate/coerce the model's JSON. Throws when the payload lacks a usable
 * summary so the caller can fall through to the next provider.
 */
export function parseAiReviewModelOutput(raw) {
    if (!isRecord(raw))
        throw new Error('AI review payload must be an object');
    const summary = cleanText(raw.summary, AI_REVIEW_LIMITS.summaryMaxChars);
    if (!summary)
        throw new Error('AI review payload is missing a summary');
    const verdictRaw = typeof raw.verdict === 'string' ? raw.verdict.trim().toLowerCase() : '';
    const findings = (Array.isArray(raw.findings) ? raw.findings : [])
        .map(parseFinding)
        .filter((finding) => finding !== null)
        .sort((a, b) => aiReviewSeverityRank(a.severity) - aiReviewSeverityRank(b.severity))
        .slice(0, AI_REVIEW_LIMITS.maxFindings);
    const testingNotes = (Array.isArray(raw.testingNotes) ? raw.testingNotes : [])
        .map((note) => cleanText(note, 300))
        .filter(Boolean)
        .slice(0, AI_REVIEW_LIMITS.testingNotes);
    const verdict = VERDICTS.includes(verdictRaw)
        ? verdictRaw
        : inferVerdict(findings);
    return { verdict, summary, findings, testingNotes };
}
export function inferVerdict(findings) {
    if (findings.some((f) => f.severity === 'critical' || f.severity === 'high'))
        return 'request_changes';
    if (findings.length)
        return 'comment';
    return 'approve';
}
export function isAiReviewPublishEvent(value) {
    return typeof value === 'string' && EVENTS.includes(value);
}
export const AI_REVIEW_SEVERITY_LABEL = {
    critical: 'Crítico',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo',
};
export const AI_REVIEW_VERDICT_LABEL = {
    approve: 'Listo para aprobar',
    comment: 'Comentarios sin bloqueo',
    request_changes: 'Pedir cambios',
};
function severityTag(severity) {
    return `**[${severity.toUpperCase()}]**`;
}
/** Markdown body posted to GitHub (`POST /pulls/{n}/reviews`). */
export function formatAiReviewMarkdown(review, options = {}) {
    const attribution = options.attribution ?? 'Zunelo · AI-assisted review';
    const lines = [`### ${attribution}`, '', review.summary.trim(), ''];
    const bodyFindings = review.findings.filter((finding) => !options.inlinePaths?.has(findingAnchor(finding)));
    if (bodyFindings.length) {
        lines.push('#### Findings', '');
        for (const finding of bodyFindings) {
            const location = finding.line ? `\`${finding.path}:${finding.line}\`` : `\`${finding.path}\``;
            lines.push(`- ${severityTag(finding.severity)} ${finding.title} — ${location}`);
            lines.push(`  ${finding.detail}`);
            if (finding.suggestion)
                lines.push(`  _Suggestion:_ ${finding.suggestion}`);
        }
        lines.push('');
    }
    if (review.testingNotes.length) {
        lines.push('#### Testing notes', '');
        for (const note of review.testingNotes)
            lines.push(`- ${note}`);
        lines.push('');
    }
    if (review.truncated) {
        lines.push(`_Diff truncated: ${review.filesReviewed} file(s) reviewed, ${review.filesSkipped.length} skipped._`, '');
    }
    lines.push(`_Generated ${review.generatedAt}${review.model ? ` · ${review.model}` : ''}_`);
    return lines.join('\n').trim();
}
export function findingAnchor(finding) {
    return `${finding.path}:${finding.line ?? 0}`;
}
/** Inline comment body for one finding. */
export function formatAiReviewFindingComment(finding) {
    const lines = [`${severityTag(finding.severity)} ${finding.title}`, '', finding.detail];
    if (finding.suggestion)
        lines.push('', `_Suggestion:_ ${finding.suggestion}`);
    return lines.join('\n');
}
//# sourceMappingURL=githubReview.js.map